import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { commonGridStyles } from 'styles/gridStyles';
import { Save, Edit } from 'lucide-react';
import { siteService } from 'components/api/sites';
import { showToast } from 'store/toastSlice';
import { useAppDispatch } from 'store/hooks';
import styles from './LevelSettings.module.scss';

interface LevelSettingsProps {
  siteId: string | number;
}

interface LevelSettingItem {
  id: number;
  key: string;
  name: string;
  tierName: string;
  tier: string;
  index: number;
  minPoints: string | number;
  spinsOnLevelUp: number;
  personalVipHost: boolean;
}

interface EditState {
  minPoints: string;
  spinsOnLevelUp: string;
}

const LevelSettings: React.FC<LevelSettingsProps> = ({ siteId }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [levels, setLevels] = useState<LevelSettingItem[]>([]);
  const [activeTier, setActiveTier] = useState<string>('');

  // Track changes per level id
  const [editedStates, setEditedStates] = useState<Record<number, EditState>>({});

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    level: LevelSettingItem | null;
    pendingState: EditState | null;
  }>({
    open: false,
    level: null,
    pendingState: null,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [updateNameModal, setUpdateNameModal] = useState({
    open: false,
    newName: '',
  });

  const fetchLevelSettings = async () => {
    try {
      setLoading(true);
      const response = await siteService.getLevelSettings(siteId);
      const data = response?.data?.levelSettings || [];
      setLevels(data);
      if (data.length > 0 && !activeTier) {
        setActiveTier(data[0].tier);
      }
      setEditedStates({});
    } catch (error: any) {
      console.error('Failed to fetch level settings:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to fetch level settings',
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevelSettings();
  }, [siteId]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTier(newValue);
  };

  // Generate unique tiers from the fetched levels, sorted by their progression index
  const dynamicTiers = useMemo(() => {
    const uniqueTiers = new Map<string, { tierName: string; index: number }>();
    levels.forEach((lvl) => {
      if (!uniqueTiers.has(lvl.tier)) {
        uniqueTiers.set(lvl.tier, { tierName: lvl.tierName, index: lvl.index });
      } else {
        // Keep the minimum index for the tier
        const existing = uniqueTiers.get(lvl.tier)!;
        if (lvl.index < existing.index) {
          existing.index = lvl.index;
        }
      }
    });

    return Array.from(uniqueTiers.entries())
      .sort((a, b) => a[1].index - b[1].index)
      .map(([tier, data]) => ({
        tier,
        tierName: data.tierName,
      }));
  }, [levels]);

  // Filter levels for the active tier tab
  const filteredLevels = useMemo(() => {
    return levels.filter((lvl) => lvl.tier.toLowerCase() === activeTier.toLowerCase());
  }, [levels, activeTier]);

  const handleInputChange = (levelId: number, field: keyof EditState, val: string) => {
    // Restrict strictly to numbers / integer digits
    if (val !== '' && !/^[0-9]+$/.test(val)) {
      return;
    }

    const original = levels.find((lvl) => lvl.id === levelId);
    if (!original) return;

    setEditedStates((prev) => {
      const currentEdit = prev[levelId] || {
        minPoints: String(original.minPoints),
        spinsOnLevelUp: String(original.spinsOnLevelUp),
      };

      const updatedEdit = {
        ...currentEdit,
        [field]: val,
      };

      // Check if values match original to clean up the changed state
      const isPointsChanged = updatedEdit.minPoints !== String(original.minPoints);
      const isSpinsChanged = updatedEdit.spinsOnLevelUp !== String(original.spinsOnLevelUp);

      const next = { ...prev };
      if (isPointsChanged || isSpinsChanged) {
        next[levelId] = updatedEdit;
      } else {
        delete next[levelId];
      }
      return next;
    });
  };

  const triggerConfirmSave = (level: LevelSettingItem) => {
    const editState = editedStates[level.id];
    if (!editState) return;

    setConfirmModal({
      open: true,
      level,
      pendingState: editState,
    });
  };

  const handleConfirmSave = async () => {
    const { level, pendingState } = confirmModal;
    if (!level || !pendingState) return;

    setIsSaving(true);
    try {
      const payload = {
        siteId: Number(siteId),
        levelKey: level.key,
        spinsOnLevelUp: Number(pendingState.spinsOnLevelUp || 0),
        minPoints: Number(pendingState.minPoints || 0),
      };

      await siteService.updateLevelSettings(payload);

      dispatch(showToast({ text: 'Level settings updated successfully', type: 'success' }));

      // Update local state and clear edit state
      setLevels((prev) =>
        prev.map((item) =>
          item.id === level.id
            ? {
                ...item,
                minPoints: payload.minPoints,
                spinsOnLevelUp: payload.spinsOnLevelUp,
              }
            : item,
        ),
      );

      setEditedStates((prev) => {
        const next = { ...prev };
        delete next[level.id];
        return next;
      });

      setConfirmModal({ open: false, level: null, pendingState: null });
    } catch (error: any) {
      console.error('Failed to update level settings:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to update level settings',
          type: 'error',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenUpdateNameModal = () => {
    const currentTierData = dynamicTiers.find((t) => t.tier === activeTier);
    setUpdateNameModal({ open: true, newName: currentTierData?.tierName || '' });
  };

  const handleUpdateName = async () => {
    if (!updateNameModal.newName.trim()) return;
    setIsSaving(true);
    try {
      const payload = {
        siteId: Number(siteId),
        tier: activeTier,
        displayName: updateNameModal.newName.trim(),
        slug: updateNameModal.newName.trim().toLowerCase(),
      };
      await siteService.updateTierName(payload);
      dispatch(showToast({ text: 'Level name updated successfully', type: 'success' }));
      setUpdateNameModal({ open: false, newName: '' });
      fetchLevelSettings(); // Refresh to get the updated tierName and slug
    } catch (error: any) {
      console.error('Failed to update level name:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to update level name',
          type: 'error',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Level Name',
        width: 140,
        sortable: false,
        renderCell: (params: any) => (
          <span className={styles.levelNameCell}>{params.row.name}</span>
        ),
      },
      {
        field: 'minPoints',
        headerName: 'Min Points',
        width: 140,
        sortable: false,
        renderCell: (params: any) => {
          const lvl = params.row;
          const editState = editedStates[lvl.id];
          const minPointsVal =
            editState !== undefined ? editState.minPoints : String(lvl.minPoints);
          return (
            <input
              type="text"
              className={styles.cellInput}
              value={minPointsVal}
              onChange={(e) => handleInputChange(lvl.id, 'minPoints', e.target.value)}
              placeholder="0"
            />
          );
        },
      },
      {
        field: 'spinsOnLevelUp',
        headerName: 'Spins on Level Up',
        width: 150,
        sortable: false,
        renderCell: (params: any) => {
          const lvl = params.row;
          const editState = editedStates[lvl.id];
          const spinsVal =
            editState !== undefined ? editState.spinsOnLevelUp : String(lvl.spinsOnLevelUp);
          return (
            <input
              type="text"
              className={styles.cellInput}
              value={spinsVal}
              onChange={(e) => handleInputChange(lvl.id, 'spinsOnLevelUp', e.target.value)}
              placeholder="0"
            />
          );
        },
      },
      {
        field: 'personalVipHost',
        headerName: 'Personal VIP Host',
        width: 140,
        sortable: false,
        renderCell: (params: any) => (
          <span className={styles.vipHostCell}>{params.row.personalVipHost ? 'Yes' : 'No'}</span>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        renderCell: (params: any) => {
          const lvl = params.row;
          const isChanged = editedStates[lvl.id] !== undefined;
          if (!isChanged) return null;
          return (
            <Button
              variant="contained"
              size="small"
              startIcon={<Save size={14} />}
              onClick={() => triggerConfirmSave(lvl)}
              className={styles.saveBtn}
            >
              Save
            </Button>
          );
        },
      },
    ],
    [editedStates, siteId],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.tabsContainer}>
        <Tabs
          value={activeTier}
          onChange={handleTabChange}
          aria-label="level settings tier tabs"
          variant="scrollable"
          scrollButtons="auto"
          className={styles.tierTabs}
        >
          {dynamicTiers.map(({ tier, tierName }) => (
            <Tab
              key={tier}
              label={tierName ? tierName.toUpperCase() : tier.toUpperCase()}
              value={tier}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleOpenUpdateNameModal}
          className={styles.editBtn}
          startIcon={<Edit size={16} />}
        >
          Level Name
        </Button>
      </Box>

      <Box className={styles.tableWrapper}>
        <DataGrid
          rows={filteredLevels}
          columns={columns}
          sx={commonGridStyles}
          disableColumnMenu
          autoHeight
          hideFooter
        />
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmModal.open}
        onClose={() =>
          !isSaving && setConfirmModal({ open: false, level: null, pendingState: null })
        }
        maxWidth="xs"
        fullWidth
        classes={{ paper: styles.dialogPaper }}
      >
        <DialogTitle className={styles.dialogTitle}>Confirm Save</DialogTitle>
        <DialogContent>
          <Typography className={styles.dialogText}>
            Are you sure you want to save the level settings changes for{' '}
            <strong className={styles.confirmBold}>{confirmModal.level?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            onClick={() => setConfirmModal({ open: false, level: null, pendingState: null })}
            disabled={isSaving}
            className={styles.cancelBtn}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSave}
            disabled={isSaving}
            className={styles.confirmBtn}
          >
            {isSaving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Name Dialog */}
      <Dialog
        open={updateNameModal.open}
        onClose={() => !isSaving && setUpdateNameModal((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
        classes={{ paper: styles.dialogPaper }}
      >
        <DialogTitle className={styles.dialogTitle}>Update Level Name</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'var(--text-secondary)' }}>
              New Name
            </Typography>
            <input
              type="text"
              className={styles.cellInput}
              style={{ width: '100%', padding: '10px 12px' }}
              value={updateNameModal.newName}
              onChange={(e) => setUpdateNameModal((prev) => ({ ...prev, newName: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            onClick={() => setUpdateNameModal((prev) => ({ ...prev, open: false }))}
            disabled={isSaving}
            className={styles.cancelBtn}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateName}
            disabled={isSaving || !updateNameModal.newName.trim()}
            className={styles.confirmBtn}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LevelSettings;
